import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, addDoc, updateDoc, doc, Timestamp, orderBy } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useApp } from '../App';
import { requireRole } from '../utils/roles';

export default function PollPanel() {
  const { currentTeam, isStaff, roles, userProfile } = useApp();
  const canEdit = isStaff;   // 투표 만들기 = 운영진 (W1-01)
  const [polls, setPolls] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ title: '', description: '', options: ['', ''], isAnonymous: false, deadline: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (currentTeam) loadPolls(); }, [currentTeam]);

  const loadPolls = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, 'teams', currentTeam.id, 'polls'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setPolls(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    if (!requireRole(roles, 'isStaff')) return;
    e.preventDefault();
    const validOpts = form.options.filter(o => o.trim());
    if (!form.title || validOpts.length < 2) return alert('제목과 최소 2개 옵션을 입력하세요.');
    setSaving(true);
    try {
      await addDoc(collection(db, 'teams', currentTeam.id, 'polls'), {
        teamId: currentTeam.id, title: form.title, description: form.description,
        options: validOpts.map(text => ({ id: Math.random().toString(36).substring(2, 8), text, voterIds: [] })),
        pollType: 'singleChoice', isAnonymous: form.isAnonymous, isActive: true,
        deadline: form.deadline ? Timestamp.fromDate(new Date(form.deadline)) : null,
        createdBy: auth.currentUser.uid, createdAt: Timestamp.now(), updatedAt: Timestamp.now(),
      });
      setShowForm(false);
      setForm({ title: '', description: '', options: ['', ''], isAnonymous: false, deadline: '' });
      loadPolls();
    } catch (err) { alert('등록 실패: ' + err.message); }
    setSaving(false);
  };

  const handleVote = async (pollId, optionId) => {
    const uid = auth.currentUser.uid;
    const poll = polls.find(p => p.id === pollId);
    if (!poll) return;
    try {
      const newOptions = poll.options.map(opt => {
        const alreadyVoted = (opt.voterIds || []).includes(uid);
        if (opt.id === optionId) {
          return { ...opt, voterIds: alreadyVoted ? opt.voterIds.filter(v => v !== uid) : [...(opt.voterIds || []), uid] };
        }
        return { ...opt, voterIds: (opt.voterIds || []).filter(v => v !== uid) };
      });
      await updateDoc(doc(db, 'teams', currentTeam.id, 'polls', pollId), { options: newOptions, updatedAt: Timestamp.now() });
      setPolls(prev => prev.map(p => p.id === pollId ? { ...p, options: newOptions } : p));
    } catch (err) { alert('투표 실패'); }
  };

  const uid = auth.currentUser?.uid;

  return (
    <div style={{ maxWidth: 700 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>🗳️ 투표</h2>
        {canEdit && <button onClick={() => setShowForm(!showForm)} style={s.addBtn}>+ 투표 만들기</button>}
      </div>

      {showForm && (
        <form onSubmit={handleCreate} style={s.formCard}>
          <input style={s.input} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="투표 제목 *" required />
          <textarea style={{ ...s.input, marginTop: 8, minHeight: 50, resize: 'vertical' }} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="설명 (선택)" />
          <div style={{ marginTop: 12 }}>
            <div style={{ fontSize: 12, color: '#8899AA', fontWeight: 600, marginBottom: 6 }}>선택지</div>
            {form.options.map((opt, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                <input style={{ ...s.input, flex: 1 }} value={opt} onChange={e => { const opts = [...form.options]; opts[i] = e.target.value; setForm(p => ({ ...p, options: opts })); }} placeholder={'옵션 ' + (i + 1)} />
                {form.options.length > 2 && <button type="button" onClick={() => setForm(p => ({ ...p, options: p.options.filter((_, j) => j !== i) }))} style={{ background: 'none', border: 'none', color: '#F87171', cursor: 'pointer', fontSize: 16 }}>✕</button>}
              </div>
            ))}
            <button type="button" onClick={() => setForm(p => ({ ...p, options: [...p.options, ''] }))} style={{ background: 'none', border: 'none', color: '#5BC4D8', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>+ 옵션 추가</button>
          </div>
          <div style={{ display: 'flex', gap: 16, marginTop: 12, alignItems: 'center' }}>
            <label style={{ fontSize: 13, color: '#AAB4C0', display: 'flex', alignItems: 'center', gap: 6 }}>
              <input type="checkbox" checked={form.isAnonymous} onChange={e => setForm(p => ({ ...p, isAnonymous: e.target.checked }))} /> 익명 투표
            </label>
            <input type="datetime-local" style={{ ...s.input, width: 'auto' }} value={form.deadline} onChange={e => setForm(p => ({ ...p, deadline: e.target.value }))} />
          </div>
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button type="submit" disabled={saving} style={s.submitBtn}>{saving ? '저장 중...' : '등록'}</button>
            <button type="button" onClick={() => setShowForm(false)} style={s.cancelBtn}>취소</button>
          </div>
        </form>
      )}

      {loading ? <div style={s.empty}>로딩 중...</div> : polls.length === 0 ? <div style={s.empty}><div style={{ fontSize: 40, opacity: 0.5 }}>🗳️</div>등록된 투표가 없습니다</div> :
        polls.map(poll => {
          const totalVotes = (poll.options || []).reduce((sum, o) => sum + (o.voterIds || []).length, 0);
          const isExpired = poll.deadline && (poll.deadline.toDate ? poll.deadline.toDate() : new Date(poll.deadline)) < new Date();
          return (
            <div key={poll.id} style={s.pollCard}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{poll.title}</h3>
                <div style={{ display: 'flex', gap: 6 }}>
                  {poll.isAnonymous && <span style={s.tag}>익명</span>}
                  {isExpired ? <span style={{ ...s.tag, color: '#F87171', borderColor: 'rgba(248,113,113,0.3)' }}>마감</span>
                    : poll.isActive ? <span style={{ ...s.tag, color: '#4ADE80', borderColor: 'rgba(74,222,128,0.3)' }}>진행중</span> : null}
                </div>
              </div>
              {poll.description && <div style={{ fontSize: 13, color: '#778899', marginBottom: 12 }}>{poll.description}</div>}
              {(poll.options || []).map(opt => {
                const pct = totalVotes > 0 ? Math.round(((opt.voterIds || []).length / totalVotes) * 100) : 0;
                const voted = (opt.voterIds || []).includes(uid);
                return (
                  <div key={opt.id} onClick={() => !isExpired && poll.isActive && handleVote(poll.id, opt.id)} style={{ padding: '10px 14px', marginBottom: 6, borderRadius: 8, cursor: isExpired ? 'default' : 'pointer', position: 'relative', overflow: 'hidden', border: voted ? '1px solid rgba(91,196,216,0.3)' : '1px solid rgba(91,196,216,0.06)', backgroundColor: 'rgba(255,255,255,0.02)' }}>
                    <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: pct + '%', backgroundColor: voted ? 'rgba(91,196,216,0.1)' : 'rgba(255,255,255,0.02)', transition: 'width 0.3s' }} />
                    <div style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: 14, fontWeight: voted ? 600 : 400 }}>{voted && '✓ '}{opt.text}</span>
                      <span style={{ fontSize: 12, color: '#778899', fontWeight: 600 }}>{pct}% ({(opt.voterIds || []).length})</span>
                    </div>
                  </div>
                );
              })}
              <div style={{ fontSize: 11, color: '#556677', marginTop: 8 }}>총 {totalVotes}표{poll.deadline && (' · 마감: ' + (poll.deadline.toDate ? poll.deadline.toDate() : new Date(poll.deadline)).toLocaleDateString('ko-KR'))}</div>
            </div>
          );
        })}
    </div>
  );
}

const s = {
  addBtn: { padding: '8px 16px', borderRadius: 6, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#5BC4D8', fontSize: 12, cursor: 'pointer', fontWeight: 600 },
  formCard: { padding: 20, backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, marginBottom: 16 },
  input: { width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.1)', fontSize: 14, backgroundColor: 'rgba(10,18,32,0.8)', color: '#E8ECF1', boxSizing: 'border-box' },
  submitBtn: { padding: '10px 24px', borderRadius: 8, border: 'none', backgroundColor: '#5BC4D8', color: '#0A1220', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  cancelBtn: { padding: '10px 20px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.2)', backgroundColor: 'transparent', color: '#8899AA', fontSize: 13, cursor: 'pointer' },
  pollCard: { padding: 20, backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(91,196,216,0.06)', borderRadius: 12, marginBottom: 12 },
  tag: { fontSize: 10, padding: '2px 8px', borderRadius: 8, border: '1px solid rgba(91,196,216,0.2)', color: '#5BC4D8', fontWeight: 600 },
  empty: { textAlign: 'center', padding: '60px 20px', color: '#556677', fontSize: 14 },
};
